<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { upperFirst } from 'lodash'
import ExceptionTester from '@/components/ExceptionTester.vue'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import IconIssue from '@/components/icons/IconIssue.vue'
import useAuthorization from '@/composables/useAuthorization'
import { PermissionOrganization, PermissionUser } from '@/generated/graphql'

const props = defineProps<{
  modelValue: boolean
}>()
defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const { t } = useI18n()
const { reactiveUserAuthz, reactiveOrgAuthz, hasOrgAuthz, canAccessOperatorHome } =
  useAuthorization()
const route = useRoute()

const isInOrgContext = computed(() => route.params.organizationSlug)

const showGroupDivider = computed(
  () =>
    hasOrgAuthz(PermissionOrganization.OrgProductRead) ||
    canAccessOperatorHome() ||
    hasOrgAuthz(PermissionOrganization.OrgProductionBatchRead) ||
    hasOrgAuthz(PermissionOrganization.OrgIssueRead),
)
</script>

<template>
  <v-navigation-drawer
    nav
    class="main-navigation-drawer"
    theme="dark"
    :model-value="props.modelValue"
    @update:model-value="$emit('update:modelValue', false)"
  >
    <template v-if="reactiveUserAuthz(PermissionUser.UserGlobalAdmin).value">
      <h3>{{ t('navigationDrawer.platform') }}</h3>
      <v-list density="compact">
        <router-link :to="{ name: 'admin-organizations' }">
          <v-list-item :title="upperFirst(t('entity.organization.plural'))">
            <template #prepend>
              <v-icon icon="corporate_fare" />
            </template>
          </v-list-item>
        </router-link>
        <router-link :to="{ name: 'admin-users' }">
          <v-list-item :title="upperFirst(t('entity.user.plural'))">
            <template #prepend>
              <v-icon icon="people" />
            </template>
          </v-list-item>
        </router-link>
      </v-list>
    </template>
    <template v-if="isInOrgContext">
      <h3>{{ t('navigationDrawer.organization') }}</h3>
      <v-list density="compact">
        <template v-if="reactiveOrgAuthz(PermissionOrganization.OrgAdmin).value">
          <router-link :to="{ name: 'users' }">
            <v-list-item :title="upperFirst(t('entity.user.plural'))">
              <template #prepend>
                <v-icon icon="people" />
              </template>
            </v-list-item>
          </router-link>
          <router-link :to="{ name: 'sites' }">
            <v-list-item :title="upperFirst(t('entity.site.plural'))">
              <template #prepend>
                <v-icon icon="factory" />
              </template>
            </v-list-item>
          </router-link>
          <router-link :to="{ name: 'stepTypes' }">
            <v-list-item :title="upperFirst(t('entity.stepType.plural'))">
              <template #prepend>
                <v-icon icon="list" />
              </template>
            </v-list-item>
          </router-link>
          <router-link :to="{ name: 'icons' }">
            <v-list-item :title="upperFirst(t('entity.icon.plural'))">
              <template #prepend>
                <v-icon icon="emoji_symbols" />
              </template>
            </v-list-item>
          </router-link>
          <router-link :to="{ name: 'organizationSettings' }">
            <v-list-item :title="t('view.organization.settings.title')">
              <template #prepend>
                <v-icon icon="tune" />
              </template>
            </v-list-item>
          </router-link>
        </template>
        <router-link
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgProductWrite).value"
          :to="{ name: 'tools' }"
        >
          <v-list-item :title="upperFirst(t('entity.tool.plural'))">
            <template #prepend>
              <v-icon icon="handyman" />
            </template>
          </v-list-item>
        </router-link>
        <router-link
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgArticleRead).value"
          :to="{ name: 'articles' }"
        >
          <v-list-item :title="upperFirst(t('entity.article.plural'))">
            <template #prepend>
              <v-icon icon="settings" />
            </template>
          </v-list-item>
        </router-link>
        <router-link
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgAdmin).value"
          :to="{ name: 'issueLabels' }"
        >
          <v-list-item :title="upperFirst(t('entity.issueLabel.plural'))">
            <template #prepend>
              <v-icon icon="label" />
            </template>
          </v-list-item>
        </router-link>
        <router-link
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgUploadIndex).value"
          :to="{ name: 'uploads' }"
        >
          <v-list-item :title="upperFirst(t('entity.upload.plural'))">
            <template #prepend>
              <v-icon icon="cloud_upload" />
            </template>
          </v-list-item>
        </router-link>
        <router-link
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgProductRead).value"
          :to="{ name: 'library' }"
        >
          <v-list-item :title="upperFirst(t('view.organization.library.title'))">
            <template #prepend>
              <v-icon icon="library_books" />
            </template>
          </v-list-item>
        </router-link>

        <v-divider v-if="showGroupDivider" class="my-4 mx-1" />

        <router-link
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgProductRead).value"
          :to="{ name: 'products' }"
        >
          <v-list-item :title="upperFirst(t('entity.product.plural'))">
            <template #prepend>
              <v-icon icon="precision_manufacturing" />
            </template>
          </v-list-item>
        </router-link>
        <router-link v-if="canAccessOperatorHome()" :to="{ name: 'operatorHome' }">
          <v-list-item :title="t('view.organization.home.operatorTitle')">
            <template #prepend>
              <v-icon icon="warehouse" />
            </template>
          </v-list-item>
        </router-link>
        <router-link
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgProductionBatchRead).value"
          :to="{ name: 'production-batches' }"
        >
          <v-list-item :title="upperFirst(t('entity.productionBatch.plural'))">
            <template #prepend>
              <v-icon icon="library_add_check" />
            </template>
          </v-list-item>
        </router-link>
        <router-link
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgIssueRead).value"
          :to="{ name: 'issues' }"
        >
          <v-list-item :title="upperFirst(t('entity.issue.plural'))">
            <template #prepend>
              <icon-issue />
            </template>
          </v-list-item>
        </router-link>
      </v-list>
    </template>
    <exception-tester />
  </v-navigation-drawer>
</template>

<style lang="scss">
@media print {
  .main-navigation-drawer {
    display: none !important;
  }
}
.main-navigation-drawer .v-navigation-drawer__content {
  padding: 15px 5px;

  a,
  a:visited {
    color: rgb(var(--v-theme-on-primary));
    text-decoration: none;
  }
}
</style>
